import { ChangeSource } from '@modules/projects/shared/enums/change-source.enum';
import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { TaskSidenavService } from '@shared/modules/task-sidenav/services/task-sidenav.service';
import { StorageService } from '@core/services/storage.service';
import { ProjectChangesSettlementStatus } from '@modules/projects/shared/enums/project-change-settlement-status.enum';
import { TranslateService } from '@ngx-translate/core';
import { TaskSelectionV2ChangeComponent } from '@shared/modules/tasks/components/task-selection-change-v2/task-selection-change-v2.component';
import { TaskInfluence } from '@shared/enums/task-influence.enum';
import { TASK_SIDENAV_FORM } from '@shared/modules/task-sidenav/const/task-sidenav-form';
import { priceToPenny } from '@shared/helpers/price-to-penny.helper';
import { ChangeDetectorRef } from '@angular/core';

@Component({
  selector: 'task-changes-summary',
  templateUrl: './task-changes-summary.component.html',
  styleUrls: ['./task-changes-summary.component.scss']
})
export class TaskChangesSummaryComponent implements OnInit, AfterViewInit {
  ChangeSource = ChangeSource;
  protocolPreviewPermission: boolean = false;
  TaskInfluence = TaskInfluence;

  @ViewChild('financeEditor') financeEditor?: TaskSelectionV2ChangeComponent;
  @ViewChild('daysEditor') daysEditor?: TaskSelectionV2ChangeComponent;

  protected currentFinanceState: TaskInfluence = TaskInfluence.NO_CHANGES;
  protected currentDateState: TaskInfluence = TaskInfluence.NO_CHANGES;
  private initialFinanceValue: { selectId: TaskInfluence | null; input: string } | null = null;
  private initialDaysValue: { selectId: TaskInfluence | null; input: string } | null = null;

  settlementsItems = [
    { name: 'Projects.ProjectSettlement.null', value: ProjectChangesSettlementStatus.CHANGE_SETTLEMENT_NONE },
    {
      name: 'Projects.ProjectSettlement.unsettled',
      value: ProjectChangesSettlementStatus.CHANGE_SETTLEMENT_NOT_SETTLED
    },
    {
      name: 'Projects.ProjectSettlement.settled',
      value: ProjectChangesSettlementStatus.CHANGE_SETTLEMENT_SETTLED
    }
  ].map((i) => {
    i.name = this.t.instant(i.name);
    return i;
  });

  constructor(
    public service: TaskSidenavService,
    public store: StorageService,
    private t: TranslateService,
    private changes: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.syncInitialStates();
  }

  ngAfterViewInit(): void {
    if (this.shouldRenderInlineEditors) {
      this.populateEditorForm('finance');
      this.populateEditorForm('days');
    }
  }

  get allowInlineEditing(): boolean {
    return !this.service?.task?.isDeleted;
  }

  get shouldRenderInlineEditors(): boolean {
    return this.allowInlineEditing;
  }

  get hasPendingInlineChanges(): boolean {
    return this.hasFinanceChange || this.hasDaysChange;
  }

  get financeCurrencyDisplayPrefix(): string {
    return this.currentFinanceState === TaskInfluence.MINUS ? '-' : '';
  }

  private get hasFinanceChange(): boolean {
    return this.isEditorChanged('finance');
  }

  private get hasDaysChange(): boolean {
    return this.isEditorChanged('days');
  }

  get financialTypeLabel(): string {
    if (this.service.task?.price === 0) {
      return this.t.instant('Financial.withoutInfluence');
    }

    if ((this.service.task?.price || 0) > 0) {
      return this.t.instant('Financial.additionalCost');
    }

    return this.t.instant('Financial.savedCost');
  }

  protected startFinanceEdit(): void {
    if (this.shouldRenderInlineEditors) return;

    this.syncInitialStates();
  }

  protected startDaysEdit(): void {
    if (this.shouldRenderInlineEditors) return;

    this.syncInitialStates();
  }

  protected onInfluenceChange({ id }: { id: TaskInfluence }, editorName: 'finance' | 'days'): void {
    const editor = editorName === 'finance' ? this.financeEditor : this.daysEditor;
    if (!editor) return;

    switch (id) {
      case TaskInfluence.NO_CHANGES:
        editor.form.get('input')?.setValue(editorName === 'finance' ? '0.00' : '0');
        break;
      case TaskInfluence.NO_ESTIMATION:
        editor.form.get('input')?.setValue(null);
        break;
      default:
        if (Number(editor.form.get('input')?.value || 0) === 0) {
          editor.form.get('input')?.setValue('');
        }
        setTimeout(() => editor.input?.inputv2?.nativeElement?.focus(), 50);
        break;
    }

    if (editorName === 'finance') this.currentFinanceState = id;
    else this.currentDateState = id;
  }

  discardInlineChanges(): void {
    if (this.financeEditor?.form) {
      this.financeEditor?.form?.reset({
        select: this.getSelectPriceValue(),
        input: this.getInputPriceValue()
      });
      this.financeEditor?.form?.markAsPristine();
    }

    if (this.daysEditor?.form) {
      this.daysEditor?.form?.reset({
        select: this.getSelectDaysValue(),
        input: this.getInputDaysValue()
      });
      this.daysEditor?.form?.markAsPristine();
    }

    this.syncInitialStates();
  }

  saveInlineChanges(emitEvent: boolean = true): void {
    if (!this.hasPendingInlineChanges) return;

    if (this.hasFinanceChange && this.financeEditor?.form?.valid) {
      const priceValue = priceToPenny(this.getInfluenceCount(this.financeEditor, this.currentFinanceState));
      const isEstimated = this.financeEditor.form.value?.select?.id !== TaskInfluence.NO_ESTIMATION;

      this.service.task.price = Number(priceValue || 0);
      this.service.task.isPriceEstimated = isEstimated;
      this.service.form.patchValue(
        {
          [TASK_SIDENAV_FORM.price]: Number(priceValue || 0),
          [TASK_SIDENAV_FORM.isPriceEstimated]: Number(isEstimated)
        },
        { emitEvent }
      );

      this.financeEditor.form.markAsPristine();
    }

    if (this.hasDaysChange && this.daysEditor?.form?.valid) {
      const additionalTime = this.getInfluenceCount(this.daysEditor, this.currentDateState);
      const isEstimated = this.daysEditor.form.value?.select?.id !== TaskInfluence.NO_ESTIMATION;

      this.service.task.additionalTime = Number(additionalTime || 0);
      this.service.task.isAdditionalTimeEstimated = isEstimated;
      this.service.form.patchValue(
        {
          [TASK_SIDENAV_FORM.additionalTime]: Number(additionalTime || 0),
          [TASK_SIDENAV_FORM.isAdditionalTimeEstimated]: Number(isEstimated)
        },
        { emitEvent }
      );

      this.daysEditor.form.markAsPristine();
    }

    this.syncInitialStates();
    this.populateEditorForm('finance');
    this.populateEditorForm('days');
  }

  private isEditorChanged(editorName: 'finance' | 'days'): boolean {
    const editor = editorName === 'finance' ? this.financeEditor : this.daysEditor;
    const initialValue = editorName === 'finance' ? this.initialFinanceValue : this.initialDaysValue;

    if (!editor?.form || !initialValue) return false;

    const currentValue = this.normalizeEditorValue(
      editor.form.get('select')?.value,
      editor.form.get('input')?.value
    );

    return currentValue.selectId !== initialValue.selectId || currentValue.input !== initialValue.input;
  }

  private normalizeEditorValue(select: any, input: any): { selectId: TaskInfluence | null; input: string } {
    return {
      selectId: select?.id ?? null,
      input: input === null || input === undefined ? '' : String(input)
    };
  }

  private populateEditorForm(editorName: 'finance' | 'days'): void {
    this.changes.detectChanges();

    requestAnimationFrame(() => {
      const editor = editorName === 'finance' ? this.financeEditor : this.daysEditor;
      if (!editor?.form) return;

      editor.form.setValue({
        select: editorName === 'finance' ? this.getSelectPriceValue() : this.getSelectDaysValue(),
        input: editorName === 'finance' ? this.getInputPriceValue() : this.getInputDaysValue()
      });
      editor.form.markAsPristine();
      editor.form.markAsUntouched();
    });
  }

  private syncInitialStates(): void {
    this.currentFinanceState = this.getSelectPriceValue().id;
    this.currentDateState = this.getSelectDaysValue().id;
    this.initialFinanceValue = this.normalizeEditorValue(
      this.getSelectPriceValue(),
      this.getInputPriceValue()
    );
    this.initialDaysValue = this.normalizeEditorValue(this.getSelectDaysValue(), this.getInputDaysValue());
  }

  private getSelectPriceValue() {
    if (!this.service?.task?.isPriceEstimated) return this.service.financial[0];
    if ((this.service?.task?.price || 0) > 0) return this.service.financial[1];
    if ((this.service?.task?.price || 0) < 0) return this.service.financial[2];
    if (this.service?.task?.price === 0) return this.service.financial[3];
    return this.service.financial[0];
  }

  private getSelectDaysValue() {
    const time = Number(this.service?.task?.additionalTime?.toString());
    if (!this.service?.task?.isAdditionalTimeEstimated) return this.service.days[0];
    if (time > 0) return this.service.days[1];
    if (time < 0) return this.service.days[2];
    if (time === 0) return this.service.days[3];
    return this.service.days[0];
  }

  protected get readOnlyDaysLabel(): string {
    return this.getSelectDaysValue()?.label || '';
  }

  private getInputDaysValue() {
    if (!this.service?.task?.isAdditionalTimeEstimated) return null;
    return Math.abs(Number(this.service.task?.additionalTime || 0));
  }

  private getInputPriceValue() {
    if (!this.service?.task?.isPriceEstimated) return null;
    return Math.abs(Number(this.service?.task?.price || 0)) / 100;
  }

  private getInfluenceCount(editor: TaskSelectionV2ChangeComponent, state: TaskInfluence) {
    const input = editor.form.get('input')?.value;
    let value: any = Math.abs(Number(input || 0));

    switch (state) {
      case TaskInfluence.PLUS:
        break;
      case TaskInfluence.MINUS:
        value = value * -1;
        break;
      case TaskInfluence.NO_CHANGES:
        value = 0;
        break;
      case TaskInfluence.NO_ESTIMATION:
        value = '';
        break;
      default:
        break;
    }

    return value;
  }
}
